import moment from 'moment'
import lodash from 'lodash'
import type { ActivityOptions, ActivityBtn, ContainActivityBtn, CourseItem } from './types'
import { EnumActivityBtnType, EnumSignStatusType } from '@xt/client/enums/activity/index'
import { getServerTime } from '@xt/client/experimental/server-time'
import { $service } from '@xt/client/experimental/request/service'
import { EnumApiCourse } from '@xt/client/api/course'
import { BindAll } from 'lodash-decorators'

// 格式化倒计时数据
export const formatEndTimeCountdown = (endTimestamp: number) => {
  const now = getServerTime('moment')
  const end = moment(endTimestamp)

  const duration = moment.duration(end.diff(now))

  return {
    isEnd: now.isAfter(end),
    days: Math.floor(duration.asDays()),
    hours: Math.floor(duration.asHours()),
    minutes: duration.minutes(),
    seconds: duration.seconds()
  }
}

@BindAll()
export class ActivityUtils {
  // 已购买课程ids - 在Mixins中完成数据初始化
  hasBuyIds: number[] = []

  constructor() {}

  // 获取已经买过的课程id
  public async loadCourseListHasbuy() {
    const [hasbuyIdsError, hasbuyIds] = await $service<Array<number>>({
      url: EnumApiCourse.CourseListHasbuy
    })

    if (hasbuyIdsError) return

    this.hasBuyIds = hasbuyIds
  }

  // 获取赠课按钮状态
  public getGiftStatus(data: { convertEndTime: number; signupStartTime: number }): boolean {
    const now = getServerTime('moment')
    const { convertEndTime, signupStartTime } = data
    const convertEndTimeMoment = moment(convertEndTime)
    const signupStartTimeMoment = moment(signupStartTime)

    return now.isAfter(signupStartTimeMoment) && now.isBefore(convertEndTimeMoment)
  }

  // 获取报名操作计算结果
  public getSignStatus(data: ActivityOptions): EnumSignStatusType | void {
    const now = getServerTime('moment')
    const { id, convertEndTime, signupStartTime, signupEndTime, endTime, courses } = data

    const convertEndTimeMoment = moment(convertEndTime)
    const signupStartTimeMoment = moment(signupStartTime)
    const signupEndTimeMoment = moment(signupEndTime)
    const endTimeMoment = moment(endTime)

    const allCourseHasbuy = lodash.includes(this.hasBuyIds, id)
    // 未购买，且报名时间已结束
    const hasEndCourse = courses.some(v => {
      return !lodash.includes(this.hasBuyIds, v.id) && now.isAfter(moment(v.signupEndTime))
    })
    // 是否有购买
    const hasPer = !!courses.filter(v => lodash.includes(this.hasBuyIds, v.id)).length

    if (now.isAfter(convertEndTimeMoment) && now.isAfter(endTimeMoment)) return

    if (allCourseHasbuy) return EnumSignStatusType.Jump

    if (now.isAfter(signupEndTimeMoment) && now.isBefore(convertEndTimeMoment)) {
      // 外部调用时已判断是否登录，所以这里不判断
      if (!hasEndCourse && hasPer) return EnumSignStatusType.Sign

      return EnumSignStatusType.NoConvert
    }

    if (now.isAfter(signupStartTimeMoment) && now.isBefore(signupEndTimeMoment)) {
      return EnumSignStatusType.Sign
    }
  }

  // 获取课程按钮配置
  public mapCourseBtn(data: ActivityOptions, { isLogin }: { isLogin: boolean }): ActivityBtn {
    const now = getServerTime('moment')
    const { id, convertEndTime, signupStartTime, signupEndTime, startTime, endTime, courses } = data

    const convertEndTimeMoment = moment(convertEndTime)
    const signupStartTimeMoment = moment(signupStartTime)
    const signupEndTimeMoment = moment(signupEndTime)
    const startTimeMoment = moment(startTime)
    const endTimeMoment = moment(endTime)

    const allCourseHasbuy = lodash.includes(this.hasBuyIds, id)
    // 未购买，且报名时间已结束
    const hasEndCourse = courses.some(v => {
      return !lodash.includes(this.hasBuyIds, v.id) && now.isAfter(moment(v.signupEndTime))
    })
    // 是否有购买
    const hasPer = !!courses.filter(v => lodash.includes(this.hasBuyIds, v.id)).length

    // 当前时间大于转报结束时间且课程结束则 已结课
    if (now.isAfter(convertEndTimeMoment) && now.isAfter(endTimeMoment)) {
      return {
        type: EnumActivityBtnType.ClassEnd,
        label: '已结课'
      }
    }

    if (allCourseHasbuy) {
      if (now.isAfter(signupEndTimeMoment) && now.isBefore(convertEndTimeMoment)) {
        return {
          type: EnumActivityBtnType.GoClass,
          label: '已报名,去上课',
          countdown: {
            endTime: convertEndTime
          }
        }
      }
      return {
        type: EnumActivityBtnType.GoClass,
        label: '已报名,去上课'
      }
    }

    // 当前时间大于转报结束时间且大于报名结束时间
    if (now.isAfter(convertEndTimeMoment) && now.isAfter(signupEndTimeMoment)) {
      // 且课程已开启则 开课中
      if (now.isAfter(startTimeMoment)) {
        return {
          type: EnumActivityBtnType.InClass,
          label: '开课中'
        }
      } else {
        return {
          type: EnumActivityBtnType.ClassEnd,
          label: '报名结束'
        }
      }
    }

    // 转报全阶
    if (now.isAfter(signupEndTimeMoment) && now.isBefore(convertEndTimeMoment)) {
      if ((!hasEndCourse && hasPer) || !isLogin)
        return {
          type: EnumActivityBtnType.Convert,
          label: '转报全阶',
          countdown: {
            endTime: convertEndTime
          }
        }

      return {
        type: EnumActivityBtnType.NoConvert,
        label: '不满足转报全阶要求'
      }
    }

    if (now.isAfter(signupStartTimeMoment) && now.isBefore(signupEndTimeMoment)) {
      if (!hasEndCourse && hasPer)
        return {
          type: EnumActivityBtnType.Convert,
          label: '转报全阶',
          countdown: {
            endTime: signupEndTime
          }
        }

      return {
        type: EnumActivityBtnType.Signup,
        label: '我要报名',
        countdown: {
          endTime: signupEndTime
        }
      }
    }

    return {
      type: EnumActivityBtnType.NotStarted,
      label: '即将报名',
      timeNum: signupStartTime
    }
  }

  // 获取包含课程的状态信息
  public mapContainCourseBtn(data: CourseItem): ContainActivityBtn {
    const now = getServerTime('moment')
    const { signupStartTime, signupEndTime, startTime, endTime } = data

    const signupStartTimeMoment = moment(signupStartTime)
    const signupEndTimeMoment = moment(signupEndTime)
    const startTimeMoment = moment(startTime)
    const endTimeMoment = moment(endTime)

    // 当前时间大于结束时间且课程结束则 已结课
    if (now.isAfter(endTimeMoment)) {
      return {
        type: EnumActivityBtnType.ClassEnd,
        label: '已结课'
      }
    }

    // 当前时间大于结束时间且大于报名结束时间且课程已开启则 开课中
    if (now.isAfter(signupEndTimeMoment) && now.isAfter(startTimeMoment)) {
      return {
        type: EnumActivityBtnType.InClass,
        label: '开课中'
      }
    }

    // 当前时间大于结束时间且课程未开启则 报名结束
    if (now.isAfter(signupEndTimeMoment) && now.isBefore(startTimeMoment)) {
      return {
        type: EnumActivityBtnType.SignupEnd,
        label: '报名结束'
      }
    }

    if (now.isBefore(signupEndTimeMoment) && now.isAfter(signupStartTimeMoment)) {
      return {
        type: 'None',
        countdown: {
          endTime: signupEndTime
        }
      }
    }

    return {
      type: 'None',
      label: `报名时间：${signupStartTimeMoment.format('YYYY.MM.DD')}`
    }
  }

  public isAllCourseHasbuy(id: number) {
    return lodash.includes(this.hasBuyIds, id)
  }
}

const activityUtils = new ActivityUtils()

export default activityUtils
